import {
  HttpErrorResponse,
  type HttpInterceptorFn,
} from "@angular/common/http";
import { inject, LOCALE_ID } from "@angular/core";
import { Router } from "@angular/router";
import { DialogOverlayRegistry } from "@core/dialog/dialog-overlay-registry";
import { AuthStore } from "@core/services/auth.store";
import { catchError, throwError } from "rxjs";

const HTTP_STATUS_UNAUTHORIZED = 401;

const isAuthEndpoint = (url: string) => {
  const authPatterns = [
    /\/auth\/(login|logout|idp)(\b|\/|$)/i, // Auth API endpoints
    // /\/sso\/(signin-oidc|signout-callback-oidc)(\b|\/|$)/i, // SSO auth endpoints
  ];

  return authPatterns.some((pattern) => pattern.test(url));
};

/**
 * Auth interceptor for cookie-based authentication via Gateway BFF
 *
 * The Gateway handles:
 * - HttpOnly session cookies (sala_session_id)
 * - Automatic token refresh at middleware level
 * - CSRF protection (sala_csrf cookie)
 *
 * This interceptor:
 * - Ensures withCredentials is set for all requests (required for cookies)
 * - Redirects to login on 401 errors (token refresh already handled by backend)
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);
  const dialogOverlayRegistry = inject(DialogOverlayRegistry);
  const localeId = inject(LOCALE_ID);
  // Clone the request and add withCredentials for cookie support
  const authReq = req.clone({
    withCredentials: true,
    setHeaders: {
      "X-SaLa-Locale": localeId,
    },
  });

   return next(authReq).pipe(
    catchError((error) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== HTTP_STATUS_UNAUTHORIZED) {
        return throwError(() => error);
      }

      // Don't redirect on auth endpoint failures
      if (isAuthEndpoint(authReq.url)) {
        return throwError(() => error);
      }

      // For other 401 errors, clear user state and redirect to login
      // This likely means the session has expired and token refresh (handled by backend) has failed
      if (error.status === HTTP_STATUS_UNAUTHORIZED && !authReq.url.endsWith("users/me")) {
        // Session expired and backend auto-refresh failed - redirect to login
        authStore.clearUser();
        dialogOverlayRegistry.closeAll();
        router.navigate(["/login"], {
          queryParams: { returnUrl: router.url },
        });
      }

      return throwError(() => error);
    }),
  );
};
